import { createInputTokens, createDropdownOverlayTokens } from '../tokens/components.js';
import { type SemanticTokens, SEMANTIC } from '../tokens/semantic.js';

/**
 * Multiselect Component Configuration
 */
export function createMultiselect(s: SemanticTokens) {
  const inputTokens = createInputTokens(s);
  const dropdownOverlayTokens = createDropdownOverlayTokens(s);
  return {
      root: {
        background: inputTokens.bg,
        disabledBackground: inputTokens.bgDisabled,
        filledBackground: inputTokens.bg,
        filledHoverBackground: inputTokens.bgHover,
        filledFocusBackground: inputTokens.bg,
        borderColor: inputTokens.border,
        hoverBorderColor: inputTokens.border,
        focusBorderColor: inputTokens.borderFocus,
        invalidBorderColor: inputTokens.borderInvalid,
        color: inputTokens.fg,
        disabledColor: inputTokens.fgDisabled,
        placeholderColor: inputTokens.placeholder,
        invalidPlaceholderColor: inputTokens.placeholder,
        shadow: 'none',
        paddingX: inputTokens.base.paddingX,
        paddingY: inputTokens.base.paddingY,
        borderRadius: inputTokens.base.radius,
        focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
        transitionDuration: dropdownOverlayTokens.transition,
      },
      dropdown: { width: dropdownOverlayTokens.dropdown.size, color: dropdownOverlayTokens.dropdownIconFg },
      overlay: {
        background: dropdownOverlayTokens.bg,
        borderColor: dropdownOverlayTokens.border,
        borderRadius: dropdownOverlayTokens.radius,
        color: dropdownOverlayTokens.fg,
        shadow: dropdownOverlayTokens.shadow,
      },
      list: { padding: dropdownOverlayTokens.listPadding, gap: dropdownOverlayTokens.listGap, header: { padding: dropdownOverlayTokens.headerPadding } },
      option: {
        focusBackground: dropdownOverlayTokens.option.bgFocus,
        selectedBackground: dropdownOverlayTokens.option.bgSelected,
        selectedFocusBackground: dropdownOverlayTokens.option.bgSelected,
        color: dropdownOverlayTokens.option.fg,
        focusColor: dropdownOverlayTokens.option.fgFocus,
        selectedColor: dropdownOverlayTokens.option.fgSelected,
        selectedFocusColor: dropdownOverlayTokens.option.fgSelected,
        padding: `${dropdownOverlayTokens.option.padding} ${dropdownOverlayTokens.option.padding}`,
        borderRadius: dropdownOverlayTokens.option.radius,
        gap: dropdownOverlayTokens.option.gap,
      },
      optionGroup: { background: 'transparent', color: dropdownOverlayTokens.optionGroup.fg, fontWeight: dropdownOverlayTokens.optionGroup.fontWeight, padding: `${dropdownOverlayTokens.option.padding} ${dropdownOverlayTokens.option.padding}` },
      clearIcon: { color: dropdownOverlayTokens.clearIconFg },
      chip: { borderRadius: dropdownOverlayTokens.chip.borderRadius },
      emptyMessage: { padding: `${dropdownOverlayTokens.emptyMessagePadding} ${dropdownOverlayTokens.emptyMessagePadding}` },
      colorScheme: {
        light: {
          root: {
            background: inputTokens.bg,
            disabledBackground: inputTokens.bgDisabled,
            filledBackground: inputTokens.bg,
            filledHoverBackground: inputTokens.bgHover,
            filledFocusBackground: inputTokens.bg,
            borderColor: inputTokens.border,
            hoverBorderColor: inputTokens.border,
            focusBorderColor: inputTokens.borderFocus,
            invalidBorderColor: inputTokens.borderInvalid,
            color: inputTokens.fg,
            disabledColor: inputTokens.fgDisabled,
            placeholderColor: inputTokens.placeholder,
            invalidPlaceholderColor: inputTokens.placeholder,
          },
          dropdown: { color: dropdownOverlayTokens.dropdownIconFg },
          overlay: { background: dropdownOverlayTokens.bg, borderColor: dropdownOverlayTokens.border, color: dropdownOverlayTokens.fg },
          option: {
            focusBackground: dropdownOverlayTokens.option.bgFocus,
            selectedBackground: dropdownOverlayTokens.option.bgSelected,
            selectedFocusBackground: dropdownOverlayTokens.option.bgSelected,
            color: dropdownOverlayTokens.option.fg,
            focusColor: dropdownOverlayTokens.option.fgFocus,
            selectedColor: dropdownOverlayTokens.option.fgSelected,
            selectedFocusColor: dropdownOverlayTokens.option.fgSelected,
          },
        },
      },
      css: () => `
        .p-multiselect .p-multiselect-dropdown {
          padding-right: ${dropdownOverlayTokens.triggerPaddingRight};
        }

        /* Keep the clear (×) icon clear of the (larger) dropdown chevron so they don't overlap */
        .p-multiselect .p-multiselect-clear-icon {
          inset-inline-end: calc(${dropdownOverlayTokens.dropdown.size} + ${dropdownOverlayTokens.triggerPaddingRight} + 0.25rem);
        }
          
        .p-multiselect .p-multiselect-label {
          height: 100%;
          line-height: normal;
        }
        
        /* Chevron icon size - 1.5rem (24px) */
        .p-multiselect .p-multiselect-dropdown .p-icon,
        .p-multiselect .p-multiselect-dropdown svg {
          width: ${dropdownOverlayTokens.chevron.size};
          height: ${dropdownOverlayTokens.chevron.size};
        }
        
        /* Filter/search input */
        .p-multiselect-filter {
          height: ${dropdownOverlayTokens.filter.height};
          font-size: ${dropdownOverlayTokens.filter.fontSize};
        }

        .p-multiselect .p-inputicon {
          top: 25%;
        }
        
        /* Filter search icon */
        .p-multiselect-filter-container .p-icon,
        .p-multiselect-filter-container svg {
          width: ${dropdownOverlayTokens.filter.iconSize};
          height: ${dropdownOverlayTokens.filter.iconSize};
        }

        /* Disabled chevron icon color */
        .p-multiselect.p-disabled .p-multiselect-dropdown {
          color: ${inputTokens.fgDisabled};
        }

        /* Hide header (select-all checkbox) when no filter input is present */
        .p-multiselect-header:not(:has(.p-multiselect-filter-container)) {
          display: none;
        }

        .p-multiselect-option.p-multiselect-option-selected,
        .p-multiselect-option.p-multiselect-option-selected.p-focus {
          background: ${dropdownOverlayTokens.option.bgFocus};
          color: ${dropdownOverlayTokens.option.fg};
        }

        /* Truncate long option labels so the overlay doesn't stretch wider than its trigger */
        .p-multiselect-option {
          min-width: 0;
        }
        .p-multiselect-option-label,
        .p-multiselect-option > * {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `,
      };
}
export const multiselect = createMultiselect(SEMANTIC);